import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

// Keyboard navigation for DataTable, following the WAI-ARIA data grid
// pattern: the table is one tab stop (a roving tabindex), and the arrow keys
// move between cells. Positions count the leaf header row as row 0, then the
// body rows, then the totals rows; columns are the shown leaf columns.

type Position = { row: number; col: number };

// Rows moved by Page Up / Page Down
const PAGE_ROWS = 10;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

const NAV_ATTRIBUTE = 'data-nav';

const toNav = ({ row, col }: Position) => `${row}:${col}`;

const fromNav = (nav: string | null): Position | undefined => {
  if (!nav) {
    return undefined;
  }
  const [row, col] = nav.split(':').map(Number);
  return Number.isInteger(row) && Number.isInteger(col)
    ? { row, col }
    : undefined;
};

const clamp = (value: number, max: number) => Math.max(0, Math.min(value, max));

// The cell an event happened in (or on)
const getCell = (target: EventTarget | null) =>
  target instanceof Element
    ? target.closest<HTMLElement>(`[${NAV_ATTRIBUTE}]`)
    : null;

const useGridNavigation = ({
  tableRef,
  colCount,
  bodyRowCount,
  footerRowCount,
  scrollToBodyRow,
}: {
  tableRef: RefObject<HTMLElement>;
  colCount: number;
  bodyRowCount: number;
  footerRowCount: number;
  // For virtualised tables: bring a body row that isn't drawn into view
  scrollToBodyRow?: (bodyIndex: number) => void;
}) => {
  const rowCount = 1 + bodyRowCount + footerRowCount;
  // Where Tab lands: the first body cell, else the first header
  const [active, setActive] = useState<Position>(() => ({
    row: bodyRowCount > 0 ? 1 : 0,
    col: 0,
  }));
  // The cell keyboard navigation moved to, until it's drawn and focused
  const pendingFocus = useRef<Position | undefined>();

  // Rows and columns come and go (sorting, paging, columns changing): keep
  // the tab stop on a cell that exists
  const activeRow = clamp(active.row, rowCount - 1);
  const activeCol = clamp(active.col, Math.max(colCount - 1, 0));

  const findCell = useCallback(
    (position: Position) =>
      tableRef.current?.querySelector<HTMLElement>(
        `[${NAV_ATTRIBUTE}="${toNav(position)}"]`,
      ) ?? null,
    [tableRef],
  );

  // Focus the cell navigated to once it's drawn (a virtualised row may only
  // be drawn after scrolling to it)
  useEffect(() => {
    const target = pendingFocus.current;
    if (!target) {
      return;
    }
    const cell = findCell(target);
    if (cell) {
      pendingFocus.current = undefined;
      cell.focus({ preventScroll: true });
      if (typeof cell.scrollIntoView === 'function') {
        cell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    } else if (
      scrollToBodyRow &&
      target.row >= 1 &&
      target.row <= bodyRowCount
    ) {
      scrollToBodyRow(target.row - 1);
    }
  });

  const moveTo = useCallback(
    (position: Position) => {
      const next = {
        row: clamp(position.row, rowCount - 1),
        col: clamp(position.col, colCount - 1),
      };
      pendingFocus.current = next;
      setActive(next);
    },
    [colCount, rowCount],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const cell = getCell(event.target);
      const position = cell ? fromNav(cell.getAttribute(NAV_ATTRIBUTE)) : null;
      if (!cell || !position) {
        return;
      }

      // Focus is on a control inside the cell: its keys are its own, apart
      // from Escape, which goes back to the cell
      if (event.target !== cell) {
        if (event.key === 'Escape') {
          event.preventDefault();
          cell.focus();
        }
        return;
      }

      const isCtrl = event.ctrlKey || event.metaKey;
      const lastRow = rowCount - 1;
      const lastCol = colCount - 1;
      const { row, col } = position;
      const moves: { [key: string]: Position | undefined } = {
        ArrowUp: { row: row - 1, col },
        ArrowDown: { row: row + 1, col },
        ArrowLeft: { row, col: col - 1 },
        ArrowRight: { row, col: col + 1 },
        Home: isCtrl ? { row: 0, col: 0 } : { row, col: 0 },
        End: isCtrl ? { row: lastRow, col: lastCol } : { row, col: lastCol },
        PageUp: { row: row - PAGE_ROWS, col },
        PageDown: { row: row + PAGE_ROWS, col },
      };
      const move = moves[event.key];
      if (move) {
        event.preventDefault();
        moveTo(move);
        return;
      }

      if (event.key === 'Enter' || event.key === 'F2' || event.key === ' ') {
        // Into the cell's own control (a link, an entity button...), else
        // activate what the cell's content does on click (a header sorts or
        // opens its sort menu)
        const control = cell.querySelector<HTMLElement>(FOCUSABLE);
        if (control) {
          event.preventDefault();
          control.focus();
          return;
        }
        if (row === 0 && cell.firstElementChild instanceof HTMLElement) {
          event.preventDefault();
          cell.firstElementChild.click();
        }
      }
    },
    [colCount, moveTo, rowCount],
  );

  // Clicking (or tabbing into a control in) a cell makes it the tab stop
  const onFocus = useCallback(
    (event: React.FocusEvent) => {
      const position = fromNav(
        getCell(event.target)?.getAttribute(NAV_ATTRIBUTE) ?? null,
      );
      if (
        position &&
        (position.row !== activeRow || position.col !== activeCol)
      ) {
        setActive(position);
      }
    },
    [activeCol, activeRow],
  );

  const getCellProps = useCallback(
    ({ row, col }: Position) => ({
      [NAV_ATTRIBUTE]: toNav({ row, col }),
      tabIndex: row === activeRow && col === activeCol ? 0 : -1,
      'aria-colindex': col + 1,
    }),
    [activeCol, activeRow],
  );

  return { getCellProps, onKeyDown, onFocus, rowCount };
};

export default useGridNavigation;
